import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { select, scaleBand, scaleLinear, max } from 'd3'

const FinancialBarChart = ({ data, updated, thinPadding }) => {
  const svgRef = useRef()
  const [graphWidth, setGraphWidth] = useState(0)
  const setBrowserWidth = () => {
    if (svgRef.current !== null) {
      setGraphWidth(window.innerWidth)
    }
  }
  useLayoutEffect(() => {
    if (graphWidth === 0) setBrowserWidth()
    window.addEventListener('resize', setBrowserWidth)

    return () => {
      window.removeEventListener('resize', setBrowserWidth)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!data || data.length === 0) return
    if (graphWidth === 0) setBrowserWidth()
    const svg = select(svgRef.current)
    svg.selectAll('*').remove() // Clear previous renders to prevent duplications
    const width = 418
    const height = 290
    const margin = {
      top: graphWidth < 641 ? 30 : 80,
      right: 40,
      bottom: graphWidth < 641 ? 25 : 50,
      left: 30,
    }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom
    const chart = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)
    // --- Styling Constants ---
    const highlightedTextColor = '#1E2E40'
    const normalTextColor = '#555555'
    const circleStrokeColor = '#606060'
    const dottedLineColor = '#888888'
    const yearLabelColor = '#333333'
    const patternStripeColor = '#F8F9FC'
    const patternBgColor = '#D2E2FE'
    const selectedPatternStripeColor = '#2C47F6'
    const selectedPatternBgColor = '#3B6AF7'
    const barRx = 4

    const defs = svg.append('defs')
    const uniqueId = Math.random().toString(36).substring(2, 9)

    const stripedPattern = defs
      .append('pattern')
      .attr('id', `bar-stripe-pattern-${uniqueId}`)
      .attr('width', 8)
      .attr('height', 8)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45)')

    const stripedPatternSelected = defs
      .append('pattern')
      .attr('id', `bar-stripe-pattern-selected-${uniqueId}`)
      .attr('width', 8)
      .attr('height', 8)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45)')

    stripedPattern.append('rect').attr('width', 8).attr('height', 8).attr('fill', patternBgColor)

    stripedPatternSelected
      .append('rect')
      .attr('width', 8)
      .attr('height', 8)
      .attr('fill', selectedPatternBgColor)

    stripedPattern
      .append('rect')
      .attr('width', 1.2)
      .attr('height', 8)
      .attr('fill', patternStripeColor)

    stripedPatternSelected
      .append('rect')
      .attr('width', 1.2)
      .attr('height', 8)
      .attr('fill', selectedPatternStripeColor)

    const xScale = scaleBand()
      .domain(data.map((d) => d.year))
      .range([0, innerWidth])
      .padding(updated ? 0 : 0.45)
      .paddingInner(updated ? (thinPadding ? 0.3 : 0.5) : 0.45)

    const yScale = scaleLinear()
      .domain([0, max(data, (d) => d.amount) * 1.5])
      .range([innerHeight, 0])

    chart
      .selectAll('.bar')
      .data(data)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xScale(d.year))
      .attr('y', (d) => yScale(d.amount))
      .attr('width', xScale.bandwidth() * (updated ? 1.1 : 1))
      .attr('height', (d) => innerHeight - yScale(d.amount))
      .attr('fill', (d) =>
        d.isHighlighted
          ? `url(#bar-stripe-pattern-selected-${uniqueId})`
          : `url(#bar-stripe-pattern-${uniqueId})`
      )
      .attr('rx', updated ? 0 : barRx)
      .attr('ry', updated ? 0 : barRx)

    if (updated) {
      const barWidth = xScale.bandwidth() + 5

      // Bottom border line
      chart
        .selectAll('.bar-border')
        .data(data)
        .join('line')
        .attr('class', 'bar-border')
        .attr('x1', (d) => xScale(d.year) - 15)
        .attr('x2', (d) => xScale(d.year) + 30 + barWidth)
        .attr('y1', innerHeight)
        .attr('y2', innerHeight)
        .attr('stroke', '#E7ECF3')
        .attr('stroke-width', 1)

      //Top Cap
      chart
        .selectAll('.bar-cap')
        .data(data)
        .join('rect')
        .attr('class', 'bar-cap')
        .attr('x', (d) => xScale(d.year))
        .attr('y', (d) => yScale(d.amount))
        .attr('width', barWidth)
        .attr('height', 1) // thickness of the border
        .attr('fill', '#C0CBD8')
    }

    const elementsAboveBar = chart.append('g').attr('class', 'elements-above-bars')

    const spaceBarToCircle = 5
    const circleRadius = 4.5
    const dottedLineHeight = 40
    const spaceLineToText = 6
    const amountLabelFontSize = '13px'

    // Calculate Y positions (from bar top, upwards)
    const circleCenterY = (d) => yScale(d.amount) - spaceBarToCircle - circleRadius + 20
    const dottedLineBottomY = (d) => circleCenterY(d) - circleRadius
    const dottedLineTopY = (d) => dottedLineBottomY(d) - dottedLineHeight
    const amountTextY = (d) => dottedLineTopY(d) - spaceLineToText

    elementsAboveBar
      .selectAll('.top-circle')
      .data(data)
      .join('circle')
      .attr('class', 'top-circle')
      .attr('cx', (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr('cy', circleCenterY)
      .attr('r', circleRadius)
      .attr('fill', 'white')
      .attr('stroke', circleStrokeColor)
      .attr('stroke-width', 1.5)

    elementsAboveBar
      .selectAll('.dotted-line')
      .data(data)
      .join('line')
      .attr('class', 'dotted-line')
      .attr('x1', (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr('y1', dottedLineBottomY)
      .attr('x2', (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr('y2', dottedLineTopY)
      .attr('stroke', dottedLineColor)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '2,2')

    elementsAboveBar
      .selectAll('.amount-label')
      .data(data)
      .join('text')
      .attr('class', 'amount-label')
      .attr('x', (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr('y', amountTextY)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'alphabetic')
      .attr('font-size', amountLabelFontSize)
      .attr('font-family', 'Arial, sans-serif')
      .attr('font-weight', (d) => (d.isHighlighted ? 'bold' : 'normal'))
      .attr('fill', (d) => (d.isHighlighted ? highlightedTextColor : normalTextColor))
      .text((d) => d.label)

    chart
      .selectAll('.year-label')
      .data(data)
      .join('text')
      .attr('class', 'year-label')
      .attr('x', (d) => xScale(d.year) + xScale.bandwidth() / 2)
      .attr('y', innerHeight + margin.bottom / 2 + 5)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '14px')
      .attr('font-family', 'Arial, sans-serif')
      .attr('fill', yearLabelColor)
      .text((d) => d.year)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, graphWidth])

  return <svg ref={svgRef} className="sm:mx-auto -mt-12 flex"></svg>
}

export default FinancialBarChart
